import { Utensils, Moon, BedDouble, Footprints, TrainFront, X, ChevronUp, ChevronDown, Plus } from "lucide-react";

const Venues = ({ venues }) => (
  <span className="block text-black/60 font-600 mt-0.5">
    {venues.map((v, i) => (
      <span key={v.name}>
        {i > 0 && <span className="opacity-50"> · </span>}
        <span className="font-700 text-black/80">{v.name}</span>
        <span className="opacity-80"> ({v.tag}{v.min != null ? ` · ${Math.max(v.min, 2)} min` : ""})</span>
      </span>
    ))}
  </span>
);

// A meal row inside a day card — a lunch break between two stops or a
// dinner idea after the last one. Same indent as the travel legs.
// `onMove(-1 | 1)` (lunch only) shifts the break one stop earlier / later —
// the day's clock re-flows around it. `canEarlier` / `canLater` gate the arrows.
// Dinner: the venues are "+ Add" pills (`onPick(v)` books ~1½h into the day);
// once one is `picked` the row shows it with an X that takes it out again.
export const MealRow = ({ kind, at, venues, dayNum, onDismiss, onMove, canEarlier, canLater, onPick, picked, quick = false }) => {
  const lunch = kind === "lunch";
  const iconBtn =
    "w-6 h-6 flex items-center justify-center rounded-full text-black/40 hover:text-black hover:bg-[#F1EDE3] disabled:opacity-20 disabled:hover:bg-transparent transition-colors";
  return (
    <div
      data-testid={`planner-${kind}-${dayNum}`}
      className="relative flex items-start gap-2 -mx-1 px-1 py-1.5 rounded-lg bg-[#FBFAF7] border border-dashed border-zinc-200 text-[12px] font-body leading-snug"
    >
      <span className="absolute top-1 right-1 flex items-center gap-0.5">
        {onMove && (
          <>
            <button
              type="button"
              data-testid={`planner-${kind}-up-${dayNum}`}
              onClick={() => onMove(-1)}
              disabled={!canEarlier}
              aria-label="Move lunch earlier"
              title="Move earlier"
              className={iconBtn}
            >
              <ChevronUp size={14} />
            </button>
            <button
              type="button"
              data-testid={`planner-${kind}-down-${dayNum}`}
              onClick={() => onMove(1)}
              disabled={!canLater}
              aria-label="Move lunch later"
              title="Move later"
              className={iconBtn}
            >
              <ChevronDown size={14} />
            </button>
          </>
        )}
        {onDismiss && (
          <button
            type="button"
            data-testid={`planner-${kind}-dismiss-${dayNum}`}
            onClick={onDismiss}
            aria-label={lunch ? "Hide the lunch break" : picked ? "Remove the dinner" : "Hide the dinner idea"}
            title="Hide"
            className="w-6 h-6 grid place-items-center rounded-full text-black/40 hover:text-white hover:bg-[#DF3226] transition-colors"
          >
            <X size={13} strokeWidth={2.5} />
          </button>
        )}
      </span>
      <span className="w-[14px] shrink-0" />
      <span className="font-display font-900 text-xs text-black/70 w-11 shrink-0 mt-px">{at}</span>
      <span className={`flex-1 ${onMove ? "pr-20" : "pr-7"}`}>
        <span className="font-800 text-xs inline-flex items-center gap-1.5">
          {lunch ? <Utensils size={12} className="text-[#DF3226]" /> : <Moon size={12} className="text-[#DF3226]" />}
          {lunch ? (quick ? "Quick lunch on the go · ~20 min" : "Lunch break · ~1h") : picked ? "Dinner · ~1½h" : "Dinner nearby — add one?"}
        </span>
        {picked ? (
          <span className="block mt-0.5">
            <span className="font-700 text-black/80">{picked.name}</span>
            <span className="text-black/60"> ({picked.tag}{picked.min != null ? ` · ${Math.max(picked.min, 2)} min` : ""})</span>
          </span>
        ) : !lunch && onPick && venues && venues.length ? (
          <span className="flex flex-wrap gap-1.5 mt-1.5">
            {venues.map((v) => (
              <button
                key={v.name}
                type="button"
                data-testid={`planner-dinner-add-${dayNum}-${v.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                onClick={() => onPick(v)}
                className="inline-flex items-center gap-1 font-display font-800 text-[11px] bg-white text-black px-2.5 py-1 rounded-full border border-zinc-300 hover:border-black hover:bg-black hover:text-white transition-colors"
              >
                <Plus size={11} strokeWidth={3} /> {v.name}
                <span className="font-body font-600 opacity-60">· {v.tag} · {Math.max(v.min, 2)} min</span>
              </button>
            ))}
          </span>
        ) : venues && venues.length ? (
          <Venues venues={venues} />
        ) : (
          <span className="block text-black/55 mt-0.5">{lunch ? "Grab something near your route." : "Wherever the evening takes you."}</span>
        )}
      </span>
    </div>
  );
};

// The hotel leg at the start / end of a day.
export const HotelLeg = ({ leg, name, at, back, dayNum }) => (
  <div
    data-testid={`planner-hotel-leg-${back ? "back" : "out"}-${dayNum}`}
    className="flex items-center gap-1.5 -mx-1 px-1 text-[11px] font-body font-700 text-black/55"
  >
    <span className="w-[14px] shrink-0" />
    <span className="font-display font-900 text-xs text-black/60 w-11 shrink-0">{at}</span>
    <BedDouble size={12} className="shrink-0" />
    <span className="truncate">{back ? "Back to" : "From"} {name}</span>
    <span className="opacity-60">·</span>
    {leg.mode === "walk" ? <Footprints size={12} /> : <TrainFront size={12} />}
    <span>~{leg.min} min {leg.label}</span>
  </div>
);
